// ── Garmin launch-monitor metric formatting (raw SI → display) ──
import type { GameSessionShot, GameSessionShotWind } from '@api/game-sessions-api.ts';

const GRAVITY = 9.81; // m/s²
const FLIGHT_SAMPLE_COUNT = 22;

/** Reads a numeric API value without converting missing data to zero. */
export const toNumber = (value?: string | number | null) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export type NormPoint = { x: number; y: number };

/**
 * Normalized trajectory shape from the fusion slice (`trajectory` block).
 * x: 0 (left) … 1 (right), y: 0 (tee/bottom) … 1 (far/top) — drawing coordinates,
 * not physical downrange. start → peak → end traces the on-card arc (end below the
 * peak produces the descending hook seen on a fade/slice).
 */
export type ShotTrajectory = {
  start: NormPoint;
  peak: NormPoint;
  end: NormPoint;
  shotShape?: string;
  curveStrength?: number;
  carryM?: number;
  apexM?: number;
  flightTimeS?: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isNormPoint = (p: unknown): p is NormPoint =>
  isRecord(p) && typeof p.x === 'number' && typeof p.y === 'number';

/** Reads the fusion trajectory from a shot's stored garmin payload, if present. */
export const extractTrajectory = (shot: GameSessionShot): ShotTrajectory | null => {
  const t =
    isRecord(shot.garmin_payload) && isRecord(shot.garmin_payload.trajectory)
      ? shot.garmin_payload.trajectory
      : null;
  if (!t || !isNormPoint(t.start) || !isNormPoint(t.peak) || !isNormPoint(t.end)) {
    return null;
  }
  return {
    start: t.start,
    peak: t.peak,
    end: t.end,
    shotShape: typeof t.shot_shape === 'string' ? t.shot_shape : undefined,
    curveStrength: typeof t.curve_strength === 'number' ? t.curve_strength : undefined,
    carryM: typeof t.carry_m === 'number' ? t.carry_m : undefined,
    apexM: typeof t.apex_m === 'number' ? t.apex_m : undefined,
    flightTimeS: typeof t.flight_time_s === 'number' ? t.flight_time_s : undefined,
  };
};

export type FlightPoint = { x: number; y: number; z: number };

/**
 * Estimates a flight trajectory from launch metrics (ball speed, launch angle,
 * launch direction) using drag-free projectile motion — the shot carries launch
 * metrics, not per-sample trajectory points. Returns [] when launch data missing.
 * Axes: x = lateral, y = height, z = downrange (metres).
 */
export const estimateFlightPath = (shot: GameSessionShot): FlightPoint[] => {
  const ballSpeed = toNumber(shot.ball_speed);
  const launchAngle = toNumber(shot.launch_angle);
  const launchDirection = toNumber(shot.launch_direction) ?? 0;

  if (ballSpeed == null || launchAngle == null || ballSpeed <= 0 || launchAngle <= 0) {
    return [];
  }

  const verticalRad = (launchAngle * Math.PI) / 180;
  const lateralRad = (launchDirection * Math.PI) / 180;

  const vUp = ballSpeed * Math.sin(verticalRad);
  const vHorizontal = ballSpeed * Math.cos(verticalRad);
  const vDownrange = vHorizontal * Math.cos(lateralRad);
  const vLateral = vHorizontal * Math.sin(lateralRad);

  const flightTime = (2 * vUp) / GRAVITY;
  if (!Number.isFinite(flightTime) || flightTime <= 0) return [];

  return Array.from({ length: FLIGHT_SAMPLE_COUNT + 1 }, (_, index) => {
    const t = (flightTime * index) / FLIGHT_SAMPLE_COUNT;
    return {
      x: vLateral * t,
      y: Math.max(0, vUp * t - 0.5 * GRAVITY * t * t),
      z: vDownrange * t,
    };
  });
};

const isPoint3 = (p: unknown): p is number[] =>
  Array.isArray(p) && p.length === 3 && p.every((v) => typeof v === 'number' && Number.isFinite(v));

/**
 * Index of the touchdown inside the matrix: everything past it is bounce and roll.
 * The API reports it directly; when absent, the last API point is treated as landing.
 */
export const flightEndIndex = (shot: GameSessionShot, matrix: number[][]): number => {
  const fromApi = shot.trajectory?.landing_index;
  if (typeof fromApi === 'number' && fromApi >= 0 && fromApi < matrix.length) {
    return fromApi;
  }
  return Math.max(0, matrix.length - 1);
};

/**
 * The shot's whole path as a 2D array of `[x, y, z]` triples in metres (x = lateral,
 * y = height, z = downrange): the flight, then the bounces and the roll-out.
 *
 * The API simulates the complete path. Its contract explicitly forbids replacing
 * missing launch data with a fabricated path, including when measured carry exists.
 */
export const buildFlightMatrix = (shot: GameSessionShot): number[][] => {
  const fromApi = shot.trajectory?.points;
  if (Array.isArray(fromApi) && fromApi.length && fromApi.every(isPoint3)) {
    return fromApi;
  }

  return [];
};

export type FlightGraphicPathPoint = {
  date: string;
  x: number;
  y: number;
  z: number;
};

/** Converts the same flight matrix used by the 3D view to the 2D graphic format. */
export const buildFlightGraphicData = (shot?: GameSessionShot | null): FlightGraphicPathPoint[] => {
  if (!shot) return [];

  const matrix = buildFlightMatrix(shot);
  const shotTimestamp = Date.parse(shot.recorded_at ?? shot.created_at);
  const baseTimestamp = Number.isFinite(shotTimestamp) ? shotTimestamp : 0;
  if (!matrix.length) return [];

  // The API does not provide timestamps for individual trajectory points. The chart
  // only needs monotonic dates to preserve the point order while sorting its input.
  return matrix.map(([x, y, z], index) => ({
    date: new Date(baseTimestamp + index).toISOString(),
    x,
    y,
    z,
  }));
};
export const FOCAL = 620;
export const GROUND_HALF_W = 40; // metres of fairway shown on each side of the tee line
export const PAD = 28;
export const LABEL_W = 120; // label text extends this far right of its anchor point
export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 8;

export const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

export type Vec3 = { x: number; y: number; z: number };
export type Camera = { yaw: number; pitch: number; dist: number; target: Vec3 };
export type ShotPointRow = {
  id: string;
  index: number;
  phase: 'flight' | 'roll';
  timeSeconds: number | null;
  x: number;
  y: number;
  z: number;
};

export const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
export const dot = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;
export const cross = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});
export const norm = (v: Vec3): Vec3 => {
  const len = Math.hypot(v.x, v.y, v.z) || 1;
  return { x: v.x / len, y: v.y / len, z: v.z / len };
};

/**
 * Pinhole projection of a world point (x = lateral, y = height, z = downrange,
 * metres) into an unbounded 2D plane. The SVG viewBox is fitted to the projected
 * bounding box afterwards, so the scene stays framed at any camera angle.
 * Returns null when the point sits behind the camera, so callers drop it from
 * the polyline instead of mirroring it.
 */
export const project = (p: Vec3, cam: Camera): { x: number; y: number } | null => {
  const eye: Vec3 = {
    x: cam.target.x + cam.dist * Math.sin(cam.yaw) * Math.cos(cam.pitch),
    y: cam.target.y + cam.dist * Math.sin(cam.pitch),
    z: cam.target.z - cam.dist * Math.cos(cam.yaw) * Math.cos(cam.pitch),
  };
  // World axes are right-handed (x = right of the fairway, y = up, z = downrange),
  // so the camera's right vector is up × forward. Taking forward × up instead
  // yields −x and mirrors the whole scene: a draw would be drawn as a fade.
  const forward = norm(sub(cam.target, eye));
  const right = norm(cross({ x: 0, y: 1, z: 0 }, forward));
  const up = cross(forward, right);

  const v = sub(p, eye);
  const depth = dot(v, forward);
  if (depth <= 0.1) return null;

  return {
    x: (FOCAL * dot(v, right)) / depth,
    y: -(FOCAL * dot(v, up)) / depth,
  };
};

/** SVG polyline points for a world-space polyline; empty when fully off-camera. */
export const polyline = (pts: Vec3[], cam: Camera): string =>
  pts
    .map((p) => project(p, cam))
    .filter((p): p is { x: number; y: number } => p != null)
    .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(' ');

/** "cross L→R 5.7 m/s · head 2.1 m/s" — how the wind actually acts on the ball. */
export const describeWind = (wind: GameSessionShotWind): string => {
  const parts: string[] = [];

  if (Math.abs(wind.cross_mps) >= 0.1) {
    parts.push(
      `cross ${wind.cross_mps > 0 ? 'L→R' : 'R→L'} ${Math.abs(wind.cross_mps).toFixed(1)} m/s`
    );
  }
  if (Math.abs(wind.head_mps) >= 0.1) {
    parts.push(`${wind.head_mps > 0 ? 'tail' : 'head'} ${Math.abs(wind.head_mps).toFixed(1)} m/s`);
  }

  return parts.length ? parts.join(' · ') : 'calm';
};
